import moment from 'moment-timezone'

/** Which side of the free-cancellation window a cancel falls on; the same values as the server's `order_cancelled`. */
export type CancellationFeeTier = 'more_than_24h' | 'less_than_24h'

export interface CancellationFeeAnalytics {
  /** The late-cancellation notice rendered: within the window of a known shooting start, and not a document order. */
  fee_shown: boolean
  fee_tier: CancellationFeeTier | null
  fee_pct: 0 | typeof LATE_CANCELLATION_FEE_PCT | null
  /** Whole hours to the shooting start, toward zero (negative once it started); null when there is none to count to. */
  hours_to_shoot: number | null
}

/** Free until this long before the shoot, then the fee applies. Counted in calendar days like the dialog (a DST switch makes that day 23 or 25 hours); the backend counts hours. */
export const LATE_CANCELLATION_WINDOW_DAYS = 1
/** The fee's share of the fee-bearing lines inside the window. The dialog copy and the backend config carry the same number. */
export const LATE_CANCELLATION_FEE_PCT = 75

export const NO_CANCELLATION_FEE_INFO: CancellationFeeAnalytics = { fee_shown: false, fee_tier: null, fee_pct: null, hours_to_shoot: null }

interface CancellationFeeInput {
  shootingStart: string | null
  earliestAssignmentTimezone: string | undefined
  isDocumentProduct: boolean
  now: moment.Moment
}

/**
 * The fee the broker sees in the cancellation dialog, as analytics props. One rule for the dialog's late-cancellation
 * notice and for the status card's cancel events: within 24h of a known shooting start the fee applies. Everything
 * the dialog cannot show (document orders, orders without a dated assignment) is reported as unknown, never as free.
 */
export const resolveCancellationFee = ({ shootingStart, earliestAssignmentTimezone, isDocumentProduct, now }: CancellationFeeInput): CancellationFeeAnalytics => {
  if (isDocumentProduct || !shootingStart || !earliestAssignmentTimezone) return NO_CANCELLATION_FEE_INFO

  const start = moment(shootingStart)
  const isLate = start.isSameOrBefore(moment(now).add(LATE_CANCELLATION_WINDOW_DAYS, 'day'))

  return {
    fee_shown: isLate,
    fee_tier: isLate ? 'less_than_24h' : 'more_than_24h',
    fee_pct: isLate ? LATE_CANCELLATION_FEE_PCT : 0,
    hours_to_shoot: start.diff(now, 'hours'),
  }
}
